/**
 * Dashboard — wire contract for the operator's queue of active to-dos
 * (ADR-0082). One row per live, viewer-mine to-do, sectioned by what it's
 * about (Operations / Firm Success / Misc).
 *
 *   GET /api/dashboard?viewer=<email>       → DashboardResponse
 *   GET /api/dashboard/count?viewer=<email> → DashboardCountResponse
 *   GET /api/dashboard/outside-roster       → OutsideRosterResponse (owner only)
 *
 * A row is a Task projected for display: about-id resolved to a human label +
 * the link to its detail page; `instruction` is the Task body; `threadCount`
 * lets the row show "N messages" without a per-row fetch. The Mine pane
 * omits watches — it holds only live to-dos that need the viewer's action
 * ("earn its place", per ADR-0082). The outside-roster read (#1024) is the
 * one place watches appear: the system owner's list of live tasks seated on an
 * address off the roster, which no one else can see or act on.
 *
 * Slice C (this contract): read-only render of Mine. Slice D wires the
 * inline thread + close-loop buttons against the row; Slice E adds creation;
 * Slice F adds the viewer picker (Mine + one segment per peer).
 *
 * Spec: ADR-0082 (Dashboard) · ADR-0083 (Tasks polymorphic about union).
 */

import { z } from "zod";
import { TaskPrioritySchema, TaskStatusSchema } from "./task.js";

/**
 * The three Dashboard sections rendered today. Each maps to a `Task.about.kind`
 * (or absence of one for `misc`); see `sectionForAbout` in the worker
 * composer. Future components (Engineering / Marketing / Finance / Legal) get
 * their own sections when they light up — the kind→section map widens then.
 */
export const DASHBOARD_SECTIONS = [
  "operations",
  "client-success",
  "misc",
] as const;
export const DashboardSectionSchema = z.enum(DASHBOARD_SECTIONS);
export type DashboardSection = z.infer<typeof DashboardSectionSchema>;

/** Display labels for each section (Mine pane headers). */
export const DASHBOARD_SECTION_LABELS: Record<DashboardSection, string> = {
  operations: "Operations",
  "client-success": "Firm Success",
  misc: "Misc",
};

/**
 * One row in the Dashboard Mine pane. Projection of a live Task:
 *
 *   - `aboutLabel` — human label for the Task's `about` (e.g. the Lead's
 *     client name, the Firm's name, the User's name, or the misc reference
 *     string). `null` when the linked record vanished mid-read (rare but
 *     possible — the row still renders, instruction-only).
 *   - `aboutHref` — in-app URL the operator can follow to the underlying
 *     record. `null` for misc rows (the reference is text, not a record) and
 *     for any kind whose target lacks a detail surface today (e.g. user
 *     detail is not yet built — Slice F or later).
 *   - `instruction` — the Task body (the assigner's note to the assignee).
 *   - `dueBy` / `committedBy` — the two-date model from ADR-0083 (assigner's
 *     hard date + assignee's self-paced commit). Either may be null.
 *   - `threadCount` — message count on the Task's thread; the row affordance
 *     can show "N messages" before expanding (Slice D opens the thread).
 */
export const DashboardRowSchema = z.object({
  /** Task record id, or a synthetic id for a derived row (e.g.
   *  `review:<firmId>` for a client-email review notice). */
  id: z.string(),
  /**
   * Row kind. `"task"` — the default — is a projected Task (every field below
   * is real). `"review"` (ADR-0115) is a DERIVED notice (a firm's pending
   * client-email change): it borrows the row shape but is rendered by its own
   * non-expandable branch (no thread / mark-seen / close-loop), self-heals
   * when the pending field clears, and carries no Task record. `"belt"`
   * (ADR-0116, OAS Slice 1) is the same shape of DERIVED notice for the
   * Operations Attention belt — a stalled case surfaced from a live rule; it
   * renders red off `dueBy`, carries no action (#969), self-heals when its
   * rule stops matching, and carries no Task record. `.default` keeps old payloads
   * valid.
   */
  kind: z.enum(["task", "review", "belt"]).default("task"),
  /** When the Task was created (ISO-8601). Used for the tertiary sort key. */
  created: z.string(),

  /** Slice K (ADR-0099): false when the row is NEW to the resolved viewer —
   *  their last_seen_at predates max(createdAt, lastMessageAt,
   *  significantChangeAt) excluding their own writes. Always true on surfaces
   *  without a viewer-seen context (entity panels) and when the seen layer is
   *  unavailable (no D1 binding). When false, `newActivity` says why. */
  seen: z.boolean(),

  /** WHY the row is new (ADR-0104 — the redesigned card renders newness by
   *  kind, not as one undifferentiated dot). Non-null iff `seen` is false on
   *  a viewer-seen surface:
   *    - kind "created" — the task itself is new to the viewer (assigned by
   *      the other operator; `actor` = assigner, `at` = task.created).
   *    - kind "message" — the latest unseen activity is a thread reply;
   *      `snippet` carries the latest message body (truncated server-side)
   *      so the card can show it without expanding.
   *    - kind "status"  — the other operator marked done / sent back;
   *      `action` distinguishes which.
   *  Surfaces without a seen context project null. */
  newActivity: z
    .object({
      kind: z.enum(["created", "message", "status"]),
      /** Email of the operator whose activity made the row new. */
      actor: z.string(),
      /** ISO-8601 moment of that activity. */
      at: z.string(),
      /** Latest message body (kind "message" only; truncated). */
      snippet: z.string().nullable(),
      /** The status flip (kind "status" only): mark-done | send-back. */
      action: z.string().nullable(),
    })
    .nullable()
    // Default (not just nullable) so a payload from a pre-ADR-0104 worker —
    // or a panel surface that never derives newness — parses as "nothing
    // new" instead of failing the page.
    .default(null),

  /** Task primary field — short readable label. Surfaces in the row's title
   *  column (Slice G); collapsed-row layout shows this above the instruction. */
  title: z.string(),

  /** Human label for what the Task is about. `null` if the link broke. */
  aboutLabel: z.string().nullable(),
  /** In-app route for the about target. `null` for misc and link-less kinds. */
  aboutHref: z.string().nullable(),
  /** OAS belt (#471 Slice 1): the record id of the MATTER (Lead) this row is
   *  about, so the belt card can offer a lead-keyed Pin toggle and show the
   *  pinned indicator. A pin watches the matter, not the placement — one lead
   *  with two stalled placements yields two rows sharing this id, so pinning
   *  one marks both. `null` for rows that aren't a matter (firm-level monthly
   *  update / assigned task) — those offer no Pin. `.default` keeps non-belt
   *  and pre-#471 payloads valid. */
  leadId: z.string().nullable().default(null),

  /** OAS by-firm view (2026-06-23): the firm that owns this row — the handling
   *  firm for blocks 3/6/8/10/12/13/16 and block 5's no-ack and stale rows, the
   *  originating firm for block 14, and `null` when LegalFlare owns it (blocks
   *  0/1/2/4/11/15/17 and block 5's sitting rows). This is the firm-facing
   *  classification made into a field: the by-firm subtab groups on it
   *  (non-null → that firm's bucket, null → the "LegalFlare" bucket). NOT
   *  blindly `linkedFirmId` — a sitting placement has a firm but waits on
   *  LegalFlare, so it files under LegalFlare. The unit is the row, not the
   *  record: a case that owes both an OF receipt and an LF receipt is two rows
   *  with two owners. `firmName` is the display label for
   *  the bucket header; `null` mirrors `firmId`. `.default(null)` keeps non-belt
   *  and pre-2026-06-23 payloads valid. */
  firmId: z.string().nullable().default(null),
  firmName: z.string().nullable().default(null),

  /** Assigner's instruction (Task.body). */
  instruction: z.string().nullable(),
  status: TaskStatusSchema,
  /** Tasks.Priority — Urgent | High | Normal | Low. Rendered as a pill in
   *  the row (Slice G). Defaults to Normal on the reader side. */
  priority: TaskPrioritySchema,

  /** Assigner's hard deadline (YYYY-MM-DD) or null. */
  dueBy: z.string().nullable(),
  /** Assignee's self-paced commit (YYYY-MM-DD) or null. */
  committedBy: z.string().nullable(),
  /** Assignee's snooze parking date (YYYY-MM-DD) or null. Rows whose
   *  `snoozedUntil` is strictly after today are filtered out of the default
   *  view; the "Show snoozed" toggle re-surfaces them with a pill (Slice H). */
  snoozedUntil: z.string().nullable(),
  /** True iff the row is currently parked — `snoozedUntil` is strictly after
   *  the operator's `today` at the moment the composer projected the row.
   *  Computed server-side so the web doesn't carry its own day-boundary
   *  logic. A row whose snooze has expired (re-surfaced today) carries
   *  `snoozedUntil` but `isSnoozed: false` — the pill and the Unsnooze button
   *  hide; the operator acts on it as a regular row. */
  isSnoozed: z.boolean(),

  /** Email — the row's "from", set to the assigner. */
  assigner: z.string(),
  /** Email of the assignee. Set on every Mine-pane row; `""` on a watch,
   *  which reaches a Dashboard row only through the outside-roster read. */
  assignee: z.string(),

  /** Number of messages on the Task's thread. */
  threadCount: z.number().int().nonnegative(),
});
export type DashboardRow = z.infer<typeof DashboardRowSchema>;

/**
 * Mine pane payload. One bucket per section; `mineCount` is the sum across
 * all sections — the same number the sidebar count badge renders.
 */
export const DashboardResponseSchema = z.object({
  sections: z.object({
    operations: z.array(DashboardRowSchema),
    "client-success": z.array(DashboardRowSchema),
    misc: z.array(DashboardRowSchema),
  }),
  /** Total rows across all sections. */
  mineCount: z.number().int().nonnegative(),
  /** Unseen rows in the DEFAULT view (currently-snoozed rows excluded, per
   *  ADR-0088 §4 badge-pane agreement). Slice K (ADR-0099). */
  newCount: z.number().int().nonnegative(),
});
export type DashboardResponse = z.infer<typeof DashboardResponseSchema>;

/**
 * Sidebar count payload. `count` matches `DashboardResponse.mineCount` (the
 * default-view row count, excludes future-snoozed rows). Exposed on its own
 * route so the badge can poll cheaply without rebuilding the row payload.
 * Per ADR-0088 §4 the count reflects the default view only — the operator's
 * sidebar badge agrees with what the operator sees.
 */
export const DashboardCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
  /** Unseen rows in the default view — the "M new" half of the sidebar
   *  badge's "(N · M new)". Slice K (ADR-0099). */
  newCount: z.number().int().nonnegative(),
});
export type DashboardCountResponse = z.infer<typeof DashboardCountResponseSchema>;

/**
 * Payload for `GET /api/dashboard/outside-roster` (#1024) — answered only for
 * the system owner. Every live task (snoozed included) whose assigner or
 * assignee is an address off OPERATOR_ROSTER (`isOutsideRoster`), less any row
 * already on the owner's Mine pane, in the Mine pane's sort order. Watches
 * project with `assignee: ""`. `count` is `rows.length`.
 */
export const OutsideRosterResponseSchema = z.object({
  rows: z.array(DashboardRowSchema),
  count: z.number().int().nonnegative(),
});
export type OutsideRosterResponse = z.infer<typeof OutsideRosterResponseSchema>;
